import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { ArrowRight, Check, Menu, X, PlayCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import FeaturesTabs from "@/components/FeaturesTabs";
import WorkflowVideo from "@/components/WorkflowVideo";
import InstallAppButton from "@/components/InstallAppButton";
import BankConnectDemo from "@/components/BankConnectDemo";
import StatementPreviewDemo from "@/components/StatementPreviewDemo";
import AiCategorizeDemo from "@/components/AiCategorizeDemo";

const PLANS = [
  {
    id: "starter", name: "Starter", price: 29,
    bg: "#FDE68A",  // amber-200 — warm, inviting entry tier
    description: "For sole proprietors validating their first bookkeeping workflow.",
    features: [
      "1 bank account",
      "3 months history",
      "1 team member",
      "Single workspace",
      "AI categorization (Claude Sonnet 4.5)",
      "PDF + CSV statement parsing",
      "P&L, Cash Flow, Balance Sheet, Expense reports",
      "PDF & Excel exports",
    ],
  },
  {
    id: "pro", name: "Pro", price: 79, featured: true,
    bg: "#A7F3D0",  // emerald-200 — most-popular featured tier
    description: "For growing firms managing up to 25 client books.",
    features: [
      "5 bank accounts per client workspace · add more at $3/mo",
      "60 months history",
      "5 team members · add more at $10/mo (up to 24 total)",
      "Up to 25 client workspaces",
      "Firm Clients module + review queue",
      "Everything in Starter",
      "Bank Connect via Teller (MTLS)",
      "OCR for scanned PDFs (Gemini vision)",
      "Custom categories + bulk re-categorize",
      "White-label PDF branding (logo + color)",
      "Reconciliation auto-match",
      "Push notifications",
    ],
  },
  {
    id: "business", name: "Business", price: 199,
    bg: "#BFDBFE",  // blue-200 — professional / top tier
    description: "For larger firms with 25+ clients needing dedicated support.",
    features: [
      "Unlimited bank accounts per client workspace",
      "60 months history",
      "25 team members · role-based access",
      "Unlimited client workspaces",
      "Everything in Pro",
      "Cross-tenant review queue",
      "Per-client sparklines + flag-for-review",
      "Firm-level PDF / XLSX rollup exports",
      "Shared audit timeline per client",
      "GDPR / CCPA encrypted data exports",
      "Dedicated support",
    ],
  },
];

export default function Landing() {
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const [demoLoading, setDemoLoading] = useState(false);
  const [marqueeNames, setMarqueeNames] = useState([
    "Aubrey & Co", "Northstar Holdings", "Meridian Capital", "Vellum Studio",
    "Halcyon Group", "Pinecrest Advisors", "Sienna Labs", "Coastline Ventures",
  ]);

  useEffect(() => {
    let cancelled = false;
    api.get("/public/landing-marquee")
      .then((r) => {
        if (cancelled) return;
        const list = r?.data?.names;
        if (Array.isArray(list) && list.length) setMarqueeNames(list);
      })
      .catch(() => { /* fall back to default list silently */ });
    return () => { cancelled = true; };
  }, []);

  const startDemo = async () => {
    if (demoLoading) return;
    setDemoLoading(true);
    try {
      const res = await api.post("/auth/demo");
      setSession(res.data.token, res.data.user, res.data.tenant);
      toast.success("Demo workspace ready — explore freely!");
      navigate("/app/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn’t start the demo. Try again.");
      setDemoLoading(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--ll-bg)" }}>
      {/* Top nav */}
      <header className="border-b sticky top-0 z-30 backdrop-blur" style={{ borderColor: "var(--ll-border)", background: "rgba(249,249,246,0.85)" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="landing-logo">
            <div className="w-7 h-7 grid place-items-center bg-[var(--ll-ink)] text-white"><span className="font-display font-bold text-sm">F</span></div>
            <span className="font-display font-bold text-lg">Financials Ready</span>
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm">
            <a href="#features" className="text-black hover:text-[#0b5d9e]" data-testid="nav-features-link">Features</a>
            <a href="#how" className="text-black hover:text-[#0b5d9e]" data-testid="nav-how-link">How it works</a>
            <a href="#pricing" className="text-black hover:text-[#0b5d9e]" data-testid="nav-pricing-link">Pricing</a>
          </nav>
          <div className="flex items-center gap-2">
            <InstallAppButton className="hidden md:flex px-3 py-2 hover:bg-[var(--ll-mint)] text-[var(--ll-ink-soft)]" />
            <Link to="/login" className="text-sm px-4 py-2 hover:bg-[var(--ll-mint)] transition-colors" data-testid="landing-login-link">Sign in</Link>
            <Link to="/signup" className="text-sm px-5 py-2 bg-[var(--ll-ink)] text-white hover:bg-[#0b5d9e] transition-colors flex items-center gap-1.5" data-testid="landing-signup-link">
              Start free <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="border-b" style={{ borderColor: "var(--ll-border)" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-28 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="inline-block text-[10px] uppercase tracking-[0.25em] px-2 py-1 mb-6 text-black" style={{ background: "var(--ll-mint)" }} data-testid="hero-eyebrow">
              Built for finance teams
            </div>
            <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-7xl leading-[0.95] tracking-tight" data-testid="hero-title">
              Bank statements,<br />
              <span className="italic" style={{ color: "#0b5d9e" }}>reconciled</span> with<br />
              quiet precision.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-black max-w-xl leading-relaxed" data-testid="hero-subtitle">
              Upload up to 60 months of statements across every bank account. Financials Ready
              parses them, reconciles them, and produces professional P&amp;L, balance
              sheets, and expense reports — ready for your accountant.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/signup" className="px-6 py-3 bg-[var(--ll-ink)] text-white hover:bg-[#2d6a4f] transition-colors flex items-center gap-2" data-testid="hero-cta-primary">
                Start free trial <ArrowRight size={16} />
              </Link>
              <button
                type="button"
                onClick={startDemo}
                disabled={demoLoading}
                data-testid="hero-cta-demo"
                className="px-6 py-3 text-white hover:opacity-90 transition-opacity flex items-center gap-2 disabled:opacity-60 disabled:cursor-wait font-medium"
                style={{ background: "#0b5d9e" }}
              >
                {demoLoading ? <Loader2 size={16} className="animate-spin" /> : <PlayCircle size={16} />}
                {demoLoading ? "Spinning up…" : "Try the live demo"}
              </button>
              <a href="#pricing" className="px-6 py-3 text-[var(--ll-ink-soft)] hover:text-[var(--ll-ink)] transition-colors text-sm" data-testid="hero-cta-secondary">See pricing →</a>
            </div>
            <div className="mt-10 flex flex-wrap items-center gap-6 text-xs text-black font-medium">
              <span className="flex items-center gap-1.5"><Check size={14} /> No credit card</span>
              <span className="flex items-center gap-1.5"><Check size={14} /> AI categorization included</span>
              <span className="flex items-center gap-1.5"><Check size={14} /> Cancel anytime</span>
            </div>
          </div>
          <div className="lg:col-span-5">
            <BankConnectDemo />
          </div>
        </div>
      </section>

      {/* Logos marquee */}
      <section className="border-b py-6 overflow-hidden" style={{ borderColor: "var(--ll-border)" }}>
        <div className="text-center text-[10px] uppercase tracking-[0.3em] text-black mb-3">
          Trusted by finance teams at modern businesses
        </div>
        <div className="ll-marquee gap-16 px-6">
          {[...marqueeNames, ...marqueeNames].map((n, i) => (
            <span key={i} className="font-display font-bold text-xl text-[var(--ll-ink-muted)] whitespace-nowrap">{n}</span>
          ))}
        </div>
      </section>

      {/* Features (categorized, tabbed) */}
      <FeaturesTabs />

      {/* How it works (animated walkthrough video) */}
      <WorkflowVideo />

      {/* "Try the preview" — confirm-modal animation */}
      <StatementPreviewDemo />

      {/* AI auto-categorization animation */}
      <AiCategorizeDemo />

      {/* Pricing */}
      <section id="pricing" className="border-b" style={{ borderColor: "var(--ll-border)" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="text-xs uppercase tracking-[0.2em] text-black mb-3">Pricing</div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-none">Plans that scale with your ledger.</h2>
            <p className="mt-4 text-black">Start free for 7 days. Upgrade when you&apos;re ready.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-px" style={{ background: "var(--ll-border)" }}>
            {PLANS.map((p) => (
              <div
                key={p.id}
                data-testid={`pricing-${p.id}`}
                className={`p-8 flex flex-col ${p.featured ? "ring-2 ring-[var(--ll-ink)]" : ""}`}
                style={{ background: p.bg || "var(--ll-bg-card)" }}
              >
                {p.featured && <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ochre)] mb-2">Most popular</div>}
                <div className="font-display font-bold text-2xl">{p.name}</div>
                <div className="text-sm text-[var(--ll-ink-soft)] mt-2 mb-6 leading-relaxed">{p.description}</div>
                <div className="font-display font-bold text-5xl flex items-baseline gap-1">
                  <span className="text-xl text-[var(--ll-ink-soft)]">$</span>{p.price}
                  <span className="text-base font-normal text-[var(--ll-ink-soft)] ml-1">/mo</span>
                </div>
                <ul className="space-y-2 mt-7 mb-8 text-sm">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2"><Check size={16} className="mt-0.5 shrink-0" />{f}</li>
                  ))}
                </ul>
                <Link
                  to="/signup"
                  data-testid={`pricing-cta-${p.id}`}
                  className={`mt-auto text-center px-5 py-3 transition-colors ${p.featured ? "bg-[var(--ll-ink)] text-white hover:bg-[#2d6a4f]" : "border border-[var(--ll-ink)] hover:bg-[var(--ll-mint)]"}`}
                >
                  Start with {p.name}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="text-white" style={{ background: "#0b5d9e" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-none">Stop reconciling. Start reporting.</h2>
            <p className="mt-4 text-base text-white/70 max-w-xl">7-day free trial. No credit card. Cancel before your accountant notices.</p>
          </div>
          <div className="lg:col-span-5 flex justify-start lg:justify-end">
            <Link to="/signup" className="px-7 py-3.5 bg-white text-[var(--ll-ink)] font-medium hover:bg-[var(--ll-mint)] transition-colors flex items-center gap-2" data-testid="cta-footer-signup">
              Create my workspace <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t" style={{ borderColor: "var(--ll-border)" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8 flex flex-wrap items-center justify-between gap-3 text-xs text-black">
          <div>© {new Date().getFullYear()} Financials Ready — Quiet precision for modern books.</div>
          <div className="flex items-center gap-6">
            <Link to="/docs/user-manual" className="hover:text-[var(--ll-ink)]" data-testid="footer-user-manual">User manual</Link>
            <Link to="/docs/admin-manual" className="hover:text-[var(--ll-ink)]" data-testid="footer-admin-manual">Admin manual</Link>
            <span>Privacy</span><span>Terms</span><span>Security</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
